import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Flex,
  Typography,
  Tag,
  Progress,
  Table,
  Button,
  Avatar,
  Space,
  Select,
  Spin,
} from "antd";
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  BankOutlined,
  EyeOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import InrOutlined from "../../general/components/common/InrOutlined";
import superAdminService from "../services/superAdminService";

const { Title, Text } = Typography;

function PlatformAnalyticsView({ onSelectCompany }) {
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("all");
  const [analytics, setAnalytics] = useState(null);

  const fetchAnalytics = useCallback(async (selectedPeriod = period) => {
    setLoading(true);
    try {
      const res = await superAdminService.getAnalytics({ period: selectedPeriod });
      if (res?.success && res.analytics) {
        setAnalytics(res.analytics);
      }
    } catch (err) {
      console.error("Failed to load platform analytics:", err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics(period);
  }, [period, fetchAnalytics]);

  const handlePeriodChange = (val) => {
    setPeriod(val);
    fetchAnalytics(val);
  };

  const data = analytics || {
    totalCompanies: 0,
    pendingRequests: 0,
    activeTrials: 0,
    paidActive: 0,
    suspended: 0,
    onDue: 0,
    totalRevenue: 0,
    avgOrderValue: 0,
    conversionRate: "0",
    totalTransactions: 0,
    paidTransactions: 0,
    industryDistribution: {},
    teamSizeDistribution: {},
    recentPayments: [],
    recentCompanies: [],
  };

  const industries = Object.entries(data.industryDistribution || {});
  const teamSizes = Object.entries(data.teamSizeDistribution || {});

  // Recent Payments Table Columns
  const paymentColumns = [
    {
      title: "Company",
      dataIndex: "companyCode",
      key: "companyCode",
      render: (code) => (
        <Space size={6}>
          <Text strong style={{ color: "#7c3aed" }}>{code}</Text>
          {onSelectCompany && (
            <Button size="small" type="text" icon={<EyeOutlined />} onClick={() => onSelectCompany(code)} />
          )}
        </Space>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amt) => (
        <Text strong style={{ color: "#059669" }}>
          ₹{Number(amt || 0).toLocaleString("en-IN")}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (st) => (
        <Tag color={st === "paid" ? "success" : "warning"} style={{ borderRadius: 6, fontWeight: 600, textTransform: "uppercase" }}>
          {st}
        </Tag>
      ),
    },
    {
      title: "Method",
      dataIndex: "paymentMethod",
      key: "paymentMethod",
      render: (m) => <Tag style={{ textTransform: "uppercase", fontSize: "0.72rem" }}>{m || "N/A"}</Tag>,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (dt) => <Text type="secondary" style={{ fontSize: "0.8rem" }}>{dt ? new Date(dt).toLocaleDateString() : "—"}</Text>,
    },
  ];

  return (
    <Spin spinning={loading} tip="Loading analytics...">
      <Flex vertical gap={20}>
      {/* Top Filter Bar */}
      <Flex justify="flex-end" align="center" gap={12}>
        <Flex align="center" gap={8}>
          <CalendarOutlined style={{ color: "#7c3aed", fontSize: "0.95rem" }} />
          <Text strong style={{ fontSize: "0.85rem", color: "#334155" }}>Period Filter:</Text>
          <Select
            value={period}
            onChange={handlePeriodChange}
            style={{ width: 160 }}
            dropdownMatchSelectWidth={false}
            options={[
              { value: "all", label: "All Time" },
              { value: "today", label: "Today" },
              { value: "this_week", label: "This Week" },
              { value: "this_month", label: "This Month" },
              { value: "last_month", label: "Last Month" },
              { value: "this_quarter", label: "This Quarter" },
              { value: "this_year", label: "This Year" },
            ]}
          />
          <Button
            icon={<ReloadOutlined spin={loading} />}
            onClick={() => fetchAnalytics(period)}
            style={{ borderRadius: 8 }}
            size="middle"
          />
        </Flex>
      </Flex>

      {/* Top 4 Executive KPI Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} styles={{ body: { padding: "18px 20px" } }} style={{ borderRadius: 14, background: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <Flex justify="space-between" align="center">
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: "0.8rem", fontWeight: 500 }}>
                  Gross Platform Revenue
                </Text>
                <Title level={3} style={{ margin: 0, color: "#059669", fontWeight: 700 }}>
                  ₹{Number(data.totalRevenue || 0).toLocaleString("en-IN")}
                </Title>
                <Flex align="center" gap={4} style={{ marginTop: 2 }}>
                  <Tag color="success" style={{ margin: 0, fontSize: "0.7rem", borderRadius: 4 }}>
                    <ArrowUpOutlined /> {data.paidTransactions || 0} Paid Orders
                  </Tag>
                </Flex>
              </Flex>
              <Avatar size={48} icon={<InrOutlined />} style={{ backgroundColor: "#ecfdf5", color: "#059669" }} />
            </Flex>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} styles={{ body: { padding: "18px 20px" } }} style={{ borderRadius: 14, background: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <Flex justify="space-between" align="center">
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: "0.8rem", fontWeight: 500 }}>
                  Conversion Rate
                </Text>
                <Title level={3} style={{ margin: 0, color: "#2563eb", fontWeight: 700 }}>
                  {data.conversionRate}%
                </Title>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Trials converted to paid
                </Text>
              </Flex>
              <Avatar size={48} icon={<CheckCircleOutlined />} style={{ backgroundColor: "#eff6ff", color: "#2563eb" }} />
            </Flex>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} styles={{ body: { padding: "18px 20px" } }} style={{ borderRadius: 14, background: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <Flex justify="space-between" align="center">
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: "0.8rem", fontWeight: 500 }}>
                  Total Enterprises
                </Text>
                <Title level={3} style={{ margin: 0, color: "#7c3aed", fontWeight: 700 }}>
                  {data.totalCompanies}
                </Title>
                <Flex gap={4}>
                  <Tag color="purple" style={{ margin: 0, fontSize: "0.7rem" }}>{data.activeTrials} Trials</Tag>
                  <Tag color="green" style={{ margin: 0, fontSize: "0.7rem" }}>{data.paidActive} Paid</Tag>
                </Flex>
              </Flex>
              <Avatar size={48} icon={<BankOutlined />} style={{ backgroundColor: "#f5f3ff", color: "#7c3aed" }} />
            </Flex>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} styles={{ body: { padding: "18px 20px" } }} style={{ borderRadius: 14, background: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
            <Flex justify="space-between" align="center">
              <Flex vertical gap={4}>
                <Text type="secondary" style={{ fontSize: "0.8rem", fontWeight: 500 }}>
                  Average Order Value
                </Text>
                <Title level={3} style={{ margin: 0, color: "#d97706", fontWeight: 700 }}>
                  ₹{Number(data.avgOrderValue || 0).toLocaleString("en-IN")}
                </Title>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Per completed transaction
                </Text>
              </Flex>
              <Avatar size={48} icon={<CreditCardOutlined />} style={{ backgroundColor: "#fffbeb", color: "#d97706" }} />
            </Flex>
          </Card>
        </Col>
      </Row>

      {/* Middle Grid: Industry & Team Distributions */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Tenant Industry Distribution" bordered={false} style={{ borderRadius: 14, background: "#ffffff", height: "100%" }}>
            {industries.length === 0 ? (
              <Text type="secondary">No industry breakdown available yet.</Text>
            ) : (
              <Flex vertical gap={12}>
                {industries.map(([ind, count]) => {
                  const pct = data.totalCompanies > 0 ? Math.round((count / data.totalCompanies) * 100) : 0;
                  return (
                    <div key={ind}>
                      <Flex justify="space-between" style={{ marginBottom: 4 }}>
                        <Text strong style={{ fontSize: "0.85rem", color: "#334155" }}>{ind}</Text>
                        <Text type="secondary" style={{ fontSize: "0.82rem" }}>
                          {count} companies ({pct}%)
                        </Text>
                      </Flex>
                      <Progress percent={pct} strokeColor="#7c3aed" showInfo={false} size="small" />
                    </div>
                  );
                })}
              </Flex>
            )}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="Company Team Tier Distribution" bordered={false} style={{ borderRadius: 14, background: "#ffffff", height: "100%" }}>
            {teamSizes.length === 0 ? (
              <Text type="secondary">No team size distribution available yet.</Text>
            ) : (
              <Flex vertical gap={12}>
                {teamSizes.map(([tier, count]) => {
                  const pct = data.totalCompanies > 0 ? Math.round((count / data.totalCompanies) * 100) : 0;
                  return (
                    <div key={tier}>
                      <Flex justify="space-between" style={{ marginBottom: 4 }}>
                        <Text strong style={{ fontSize: "0.85rem", color: "#334155" }}>{tier}</Text>
                        <Text type="secondary" style={{ fontSize: "0.82rem" }}>
                          {count} companies ({pct}%)
                        </Text>
                      </Flex>
                      <Progress percent={pct} strokeColor="#2563eb" showInfo={false} size="small" />
                    </div>
                  );
                })}
              </Flex>
            )}
          </Card>
        </Col>
      </Row>

      {/* Bottom Grid: Recent Transactions Table */}
      <Card
        title={
          <Flex justify="space-between" align="center">
            <span>Recent Platform Transactions</span>
            <Text type="secondary" style={{ fontSize: "0.78rem" }}>
              Latest Razorpay Billings
            </Text>
          </Flex>
        }
        bordered={false}
        style={{ borderRadius: 14, background: "#ffffff" }}
      >
        <Table
          columns={paymentColumns}
          dataSource={data.recentPayments || []}
          rowKey={(p, idx) => p._id || p.razorpayPaymentId || idx}
          pagination={false}
          size="middle"
          locale={{ emptyText: "No recent payments recorded on the platform." }}
        />
      </Card>
    </Flex>
    </Spin>
  );
}

export default PlatformAnalyticsView;
